import React from 'react';
import { Link } from 'react-router-dom';
import { campaignService } from '../services/campaignService';
import { formatCompactNumber, formatPercent } from '../utils/format';
import Badge from '../components/ui/Badge';
import Skeleton from '../components/ui/Skeleton';
import EmptyState from '../components/ui/EmptyState';
import Dropdown from '../components/ui/Dropdown';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import EventBus from '../utils/EventBus';
import styles from './Campaigns.module.css';

const FILTERS = ['all', 'draft', 'scheduled', 'sending', 'completed'];

class CampaignRowSkeleton extends React.Component {
  render() {
    return (
      <tr className={styles.skRow}>
        <td><Skeleton variant="text" width={180} height={13} /></td>
        <td><Skeleton variant="text" width={60} height={13} /></td>
        <td><Skeleton variant="text" width={50} height={13} /></td>
        <td><Skeleton variant="text" width={60} height={13} /></td>
        <td><Skeleton variant="text" width={40} height={13} /></td>
        <td><Skeleton variant="text" width={40} height={13} /></td>
        <td><Skeleton variant="text" width={50} height={13} /></td>
        <td><Skeleton variant="rect" width={52} height={24} style={{ borderRadius: 6 }} /></td>
      </tr>
    );
  }
}

class Campaigns extends React.Component {
  constructor(props) {
    super(props);
    this.state = { loading: true, filter: 'all', campaigns: [], confirmDelete: null, confirmCancel: null, acting: false };
    this.load = this.load.bind(this);
    this.act = this.act.bind(this);
  }

  componentDidMount() {
    this.load();
    EventBus.on('toast', this._maybeReload = (t) => {
      if (t?.message?.includes('launched') || t?.message === 'Draft saved.') this.load();
    });
  }

  componentWillUnmount() {
    EventBus.off('toast', this._maybeReload);
  }

  load() {
    this.setState({ loading: true });
    campaignService.getAll({ limit: 100 })
      .then((campaigns) => this.setState({ loading: false, campaigns }))
      .catch(() => this.setState({ loading: false }));
  }

  filtered() {
    const { filter, campaigns } = this.state;
    if (filter === 'all') return campaigns;
    return campaigns.filter((c) => c.status === filter);
  }

  act(promise, successMessage) {
    this.setState({ acting: true });
    promise
      .then(() => {
        this.setState({ acting: false, confirmDelete: null, confirmCancel: null });
        EventBus.emit('toast', { type: 'success', message: successMessage });
        this.load();
      })
      .catch((err) => {
        this.setState({ acting: false });
        EventBus.emit('toast', { type: 'error', message: err.message || 'Action failed.' });
      });
  }

  menuItems(c) {
    const items = [];
    if (['draft', 'scheduled', 'paused'].includes(c.status)) {
      items.push({ label: 'Edit', onClick: () => EventBus.emit('openWizard', { campaign: c }) });
    }
    items.push({
      label: 'Duplicate',
      onClick: () => this.act(campaignService.duplicate(c.id), 'Campaign duplicated.'),
    });
    if (['scheduled', 'sending'].includes(c.status)) {
      items.push({ label: 'Pause', onClick: () => this.act(campaignService.pause(c.id), 'Campaign paused.') });
    }
    if (c.status === 'paused') {
      items.push({ label: 'Resume', onClick: () => this.act(campaignService.resume(c.id), 'Campaign resumed.') });
    }
    if (!['completed', 'cancelled'].includes(c.status)) {
      items.push({ divider: true }, { label: 'Cancel', danger: true, onClick: () => this.setState({ confirmCancel: c }) });
    }
    items.push({ label: 'Delete', danger: true, onClick: () => this.setState({ confirmDelete: c }) });
    return items;
  }

  render() {
    const { loading, filter, campaigns, confirmDelete, confirmCancel, acting } = this.state;
    const rows = this.filtered();

    return (
      <>
        <div className={styles.pageHead}>
          <div>
            <div className={styles.pageTitle}>Campaigns</div>
            <div className={styles.pageSub}>All your messaging campaigns · push · in-app · WhatsApp · email</div>
          </div>
          <button
            className={styles.newBtn}
            onClick={() => EventBus.emit('openWizard', {})}
          >
            + New Campaign
          </button>
        </div>

        <div className={styles.tabs}>
          {FILTERS.map((f) => (
            <button
              key={f}
              className={[styles.tab, filter === f ? styles.tabOn : ''].join(' ')}
              onClick={() => this.setState({ filter: f })}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
              {f !== 'all' && (
                <span className={styles.tabCount}>
                  {campaigns.filter((c) => c.status === f).length}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.th}>Campaign</th>
                <th className={styles.th}>Channel</th>
                <th className={styles.th}>Status</th>
                <th className={styles.th}>Sent</th>
                <th className={styles.th}>Delivered</th>
                <th className={styles.th}>Open %</th>
                <th className={styles.th}>CTR %</th>
                <th className={styles.th}>Converted</th>
                <th className={styles.th}></th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? [0, 1, 2, 3, 4].map((i) => <CampaignRowSkeleton key={i} />)
                : rows.length === 0
                  ? (
                    <tr>
                      <td colSpan={9}>
                        <EmptyState icon="📢" title="No campaigns" description="Create your first campaign to get started." />
                      </td>
                    </tr>
                  )
                  : rows.map((c) => {
                      const openPct = c.sent_count ? (c.opened_count / c.sent_count) * 100 : null;
                      const ctrPct = c.sent_count ? (c.clicked_count / c.sent_count) * 100 : null;
                      return (
                        <tr key={c.id} className={styles.row}>
                          <td className={styles.tdName}>
                            <Link to={'/campaigns/' + c.id} style={{ color: 'inherit', textDecoration: 'none' }}>{c.name}</Link>
                          </td>
                          <td className={styles.td}>{c.channel}</td>
                          <td className={styles.td}><Badge status={c.status} dot>{c.status}</Badge></td>
                          <td className={styles.tdNum}>{formatCompactNumber(c.sent_count)}</td>
                          <td className={styles.tdNum}>{formatCompactNumber(c.delivered_count)}</td>
                          <td className={styles.tdNum}>{formatPercent(openPct)}</td>
                          <td className={styles.tdNum}>{formatPercent(ctrPct)}</td>
                          <td className={styles.tdNum}>{formatCompactNumber(c.converted_count)}</td>
                          <td className={styles.tdAct}>
                            <Dropdown
                              align="right"
                              trigger={<button className={styles.actBtn}>⋯</button>}
                              items={this.menuItems(c)}
                            />
                          </td>
                        </tr>
                      );
                    })
              }
            </tbody>
          </table>
        </div>

        <ConfirmDialog
          open={!!confirmDelete}
          title="Delete campaign?"
          message={confirmDelete ? `"${confirmDelete.name}" will be removed. This can't be undone.` : ''}
          confirmLabel="Delete"
          loading={acting}
          onConfirm={() => this.act(campaignService.remove(confirmDelete.id), 'Campaign deleted.')}
          onCancel={() => this.setState({ confirmDelete: null })}
        />

        <ConfirmDialog
          open={!!confirmCancel}
          title="Cancel campaign?"
          message={confirmCancel ? `"${confirmCancel.name}" will stop sending immediately. This can't be undone.` : ''}
          confirmLabel="Cancel campaign"
          loading={acting}
          onConfirm={() => this.act(campaignService.cancel(confirmCancel.id), 'Campaign cancelled.')}
          onCancel={() => this.setState({ confirmCancel: null })}
        />
      </>
    );
  }
}

export default Campaigns;
